<template>
  <div class="bx-content bx-search bx-typo--search">
    <div ref="rootDiv"
         :class="{
           'bx-content__search': true,
           'bx-js-filters-open': filtersOpen
         }">
      <form class="bx-form bx-typo--form bx-form--search bx-typo--form-search">
        <div class="bx-searchfield">
          <input id="searchfield_button"
                 v-model="searchPhrase"
                 type="search"
                 class="bx-form__input"
                 title="Suchbegriff eingeben"
                 aria-label="Suchbegriff eingeben"
                 placeholder="Sendung suchen"
                 @keyup.enter="doSearch('keyword')">
          <button class="bx-form__button"
                  aria-label="Suchen"
                  @click.prevent="doSearch('keyword')">
            <Icon name="search" />
          </button>
        </div>
        <div v-if="searchErrorMessage"
             class="bx-form__message bx-form__message--error">
          {{ searchErrorMessage }}
        </div>
      </form>
      <div class="bx-search__header bx-typo--search-header bx-filters bx-filters--searchandfilters">
        <a name="search-filters"
           class="bx-filter__anchor" />
        <div tabindex="0"
             role="button"
             :aria-label="ariaLabelFilters"
             :aria-expanded="filtersOpen"
             class="bx-filtertoggle"
             @click.prevent="toggleFilters"
             @keyup.enter="toggleFilters">
          <Icon name="filters"
                :class="filtersOpen ? 'icon--active' : 'icon--inactive'" />
          <span>{{ labelFilterToggle() }}</span>
        </div>
      </div>
      <div class="bx-search__wrapper">
        <div v-for="(group, index) in search?.filters"
             :id="group.id || undefined"
             :key="`filter-${index}`"
             :class="group.class">
          <fieldset v-if="filtersOpen"
                    class="bx-filter__wrapper">
            <legend>{{ group.groupName }}</legend>
            <span v-if="group.label"
                  class="bx-filter__label">
              {{ group.label }}
            </span>
            <div v-for="(item, innerIndex) in group.items"
                 :id="item.id || undefined"
                 :key="`filter-${innerIndex}`"
                 :class="`bx-filter bx-filter--${item.id}`"
                 :data-type="item.type">
              <template v-if="item.type === 'multiselect'">
                <div :id="`multiselect-${item.name}`"
                     class="bx-multiselect">
                  <div class="bx-multiselect__header"
                       tabindex="0"
                       role="button"
                       :aria-label="`Mehrfachauswahl für ${item.label}`"
                       :aria-expanded="isMultiselectOpen(`multiselect-${item.name}`)"
                       :aria-controls="`multiselect-list-${item.name}`"
                       @click="toggleMultiselect(`multiselect-${item.name}`)"
                       @keyup.enter="toggleMultiselect(`multiselect-${item.name}`)">
                    <strong>{{ item.label }}</strong>
                    <Icon name="arrow_toggle" />
                  </div>
                  <div :id="`multiselect-list-${item.name}`"
                       class="bx-multiselect__list">
                    <input v-if="item.enableItemFilter"
                           v-model="multiselectSearchQueries[item.name]"
                           type="search"
                           :placeholder="`🔍 ${item.label} suchen...`"
                           class="bx-multiselect__search">
                    <label v-for="choice in item.valueChoices"
                           :key="choice.value"
                           :for="choice.value.toString()"
                           :class="{ 'bx-js-hide': !multiselectMatchesSearch(item.name, choice.label) }">
                      {{ choice.label }}
                      <input :id="choice.value.toString()"
                             v-model="filterSettings.fieldMatches[item.fieldPath]"
                             type="checkbox"
                             :value="choice.value.toString()"
                             @click="handleMultiselectClick ($event, item.fieldPath, choice.value)">
                    </label>
                    <label v-if="countFilteredMultiselectItems(item.name) === 0"
                           class="bx-multiselect__no-results">
                      <i>{{ item.label }} nicht gefunden</i>
                    </label>
                    <div class="bx-multiselect__fade-out" />
                  </div>
                </div>
              </template>
            </div>
          </fieldset>
        </div>

        <div v-if="$slots.filtertogglebottom && filtersOpen"
             class="bx-filtertoggle--bottom">
          <slot name="filtertogglebottom" />
        </div>
      </div>

      <div v-if="multiselectTags.length > 0"
           class="bx-multiselect__tags">
        <strong>Gefiltert nach:</strong>
        <button v-for="tag in multiselectTags"
                :key="tag.value"
                :title="`Filter '${tag.label}' entfernen`"
                @click="deselectMultiselectTag(tag.field, tag.value)">
          {{ tag.label }}
          <Icon name="close" />
        </button>
      </div>

      <div v-if="numFound === 0 && !progressSearch"
           class="bx-search__no-results">
        <span>
          <strong>Keine Treffer gefunden.</strong><br><br>
          Versuche es mit einem anderen Suchbegriff <br>
          oder passe deine Filter an.
        </span>
      </div>

      <div ref="teaserGroupWrapper"
           class="bx-js-teaser-group-wrapper"
           :class="{ 'bx-js-loading': progressSearch }">
        <div v-if="progressSearch"
             class="bx-form__loader" />
        <TeaserGroup v-if="teasers.length > 0"
                     :teasers="teasers"
                     :teaser-group-type="teaserGroupType"
                     :brand="brandFromStore" />
      </div>
      <PagePagination v-if="numFound > limit"
                      :page-meta="{
                        numPages: Math.ceil(numFound / limit),
                        page: offset / limit + 1,
                        useSeparator: '#',
                        url: paginationUrl
                      }" />
    </div>
  </div>
</template>
<script>
import axios from 'axios'
import { mapState, mapActions } from 'pinia'

import EventBus from '../../../../event-bus/EventBus'
import { usePageStore } from '../../../../stores/page'
import { useConfigStore } from '../../../../stores/config'
import { useAdaStore } from '../../../../stores/ada'
import { vodSearchSettings } from '../utils/vodSearchSettings'
import { cleanUpSearchPhrase } from '../../../../utils/strings.ts'
import adaMoltenBundle from '../../../../mixins/ada-molten-bundle'
import deviceDetection from '../../../../mixins/device-detection'
import keepTabbingFocus from '../../../../mixins/keepTabbingFocus.js'
import urlHelper from '../../../../mixins/url-helper'
import Icon from '../../../../components/shared/Icon.vue'
import TeaserGroup from '../../../../components/teaser/TeaserGroup.vue'
import PagePagination from '../../../../components/PagePagination.vue'
import adaDynamicSlotMarker from '../../../../mixins/ada-dynamic-slot-marker'

export default {
  name: 'ParagraphTypeVodSearch',
  components: {
    TeaserGroup,
    PagePagination,
    Icon
  },
  mixins: [
    deviceDetection,
    adaMoltenBundle,
    urlHelper,
    keepTabbingFocus,
    adaDynamicSlotMarker
  ],
  data () {
    return {
      search: vodSearchSettings,
      filtersOpen: false,
      searchErrorMessage: '',
      searchPhrase: '',
      paginationUrl: '',
      progressSearch: false,
      offset: 0,
      limit: 25,
      numFound: 0,
      teasers: [],
      multiselectSearchQueries: {},
      multiselectOpenState: {},
      filterSettings: {
        latestSearchPhrase: '',
        fieldMatches: {},
        nestedPaths: {},
        valueTypes: {},
        ranges: {},
        pickedOrder: 0,
        checked: []
      },
      teaserGroupType: 'bx-teaser-container--vod' // used for the teaser group type, to inject the marker into the correct teaser group
    }
  },
  computed: {
    ...mapState(usePageStore, ['brandFromStore']),
    ...mapState(useConfigStore, ['rsConfig']),
    ...mapState(useAdaStore, ['isAsyncContentMode', 'dynamicAdSlotsAllocated']),
    ariaLabelFilters () {
      return this.filtersOpen ? 'Filter schliessen' : 'Filter öffnen'
    },
    reloadConfig () {
      return { ...this.rsConfig.adSlotUsage.reloadAdsOnInteraction.config, ...{ minDisplayDuration: 0, clickCount: 1 } }
    },
    multiselectTags () {
      const tags = []
      // Go through all fields with "multiobject"
      Object.entries(this.filterSettings.valueTypes).forEach(([field, type]) => {
        if (type === 'multiobject' && this.filterSettings.fieldMatches[field]) {
          // Find the right filter item in search.filters
          const filterItem = this.search.filters.flatMap(f => f.items).find(item => item.fieldPath === field)

          // If a matching filter item with valueChoices exists...
          if (filterItem && filterItem.valueChoices) {
            this.filterSettings.fieldMatches[field].forEach(value => {
              // find the right label from the valueChoices
              const valueChoice = filterItem.valueChoices.find(choice => choice.value === value)
              const label = valueChoice ? valueChoice.label : value // Fallback: Raw value if no label found
              // Special case for VOD filters: The tag bubble for 'amazon' is deliberately suppressed
              // because 'amazonprime' is already displayed and both services belong together in terms of content.
              // The goal: to avoid duplicate display in the tag bubbles.
              if (label !== 'amazon') tags.push({ field, value, label })
            })
          }
        }
      })
      return tags
    }
  },
  watch: {
    'filterSettings.checked' () {
      this.doSearch()
    },
    'filterSettings.pickedOrder' () {
      this.doSearch()
    },
    'filterSettings.latestSearchPhrase' () {
      this.searchPhraseChanged(this.filterSettings.latestSearchPhrase)
    },
    teasers (newVal) {
      if (!import.meta.env.SSR && this.isAsyncContentMode && newVal.length > 0) {
        // if we are in async content mode and there are teasers loaded, we will inject new marker to the async content container
        // the callback function contains the specific logic for the content of this component
        this.injectMarkerAndAllocateAdSlotsInDynamicContent(() => { this.injectMarkerToTeaserGroup(true) })
      }
    }
  },
  beforeMount () {
    // inform molten bundle logic, that we want to place ad slots in async content mode
    this.setAsyncContentMode(true)
  },
  mounted () {
    const searchFiltersOpen = window.localStorage.getItem('vodSearchFiltersOpen')
    if (searchFiltersOpen !== null) {
      this.filtersOpen = searchFiltersOpen === 'true'
    } else if (this.searchPhrase.length === 0) {
      this.toggleFilters()
    }
    // Click outside to close multiselects - only one multiselect can be open at a time
    // while we are using multiselects only for TVM, we add the event listener here
    document.addEventListener('click', this.closeMultiselects)

    this.initDefaults()

    if (!import.meta.env.SSR) {
      this.paginationUrl = window.location.pathname + window.location.search
      this.fetchPageOffset()

      const queryParams = this.getQueryParams()
      const storedFilters = JSON.parse(window.localStorage.getItem('vodSearchFilterSettings'))
      this.searchPhrase = decodeURIComponent(queryParams.query || '').replaceAll('+', ' ')

      if (this.searchPhrase === storedFilters?.latestSearchPhrase) {
        this.filterSettings = storedFilters
      }

      this.doSearch('pageView')

      window.addEventListener('hashchange', () => {
        this.fetchPageOffset()
        this.doSearch('hashchange')
        // trigger tracking calls (only if the content has changed due to the use of pagination - not on the initial page view)
        EventBus.$emit('triggerIVW')
        EventBus.$emit('triggerAGF')
        this.triggerGAForSearchResult()
      }, false)
    }
  },
  beforeUnmount () {
    document.removeEventListener('click', this.closeMultiselects)
  },
  methods: {
    ...mapActions(useAdaStore, ['setAsyncContentReadyForAdReload', 'setAsyncContentMode', 'flushAdSlotMarker', 'flushActivatedAdSlotMarker', 'setAsyncContentRefresh']),
    toggleFilters () {
      this.filtersOpen = !this.filtersOpen
      window.localStorage.setItem('vodSearchFiltersOpen', this.filtersOpen)
    },
    labelFilterToggle () {
      return this.filtersOpen ? 'Filter auswählen' : 'Filter anzeigen'
    },
    searchPhraseChanged (searchPhrase) {
      if (searchPhrase.length === 0) {
        this.filtersOpen = true
        this.labelFilterToggle()
      }
    },
    closeMultiselects (event) {
      const allMultiselects = document.querySelectorAll('.bx-multiselect')
      let clickedInside = false

      allMultiselects.forEach(ms => {
        if (ms.contains(event.target)) {
          clickedInside = true
          // all other multiselects should be closed, except the clicked one
          allMultiselects.forEach(other => {
            if (other !== ms) other.classList.remove('bx-js-open')
          })
        }
      })
      // if click was outside, close all multiselects
      if (!clickedInside) {
        allMultiselects.forEach(ms => ms.classList.remove('bx-js-open'))
      }
    },
    toggleMultiselect (id) {
      const isOpen = document.getElementById(id).classList.toggle('bx-js-open')
      this.multiselectOpenState[id] = isOpen
      if (isOpen) {
        this.keepTabbingFocusInMultiselect(id)
      }
    },
    isMultiselectOpen (id) {
      return this.multiselectOpenState[id] || false
    },
    keepTabbingFocusInMultiselect (id) {
      this.$nextTick(() => {
        this.removeTabbingEventListeners()
        this.keepTabbingFocus(document.getElementById(id), ['input'], 0, () => {
          this.removeTabbingEventListeners()
          this.toggleMultiselect(id)
          document.querySelector(`#${id} .bx-multiselect__header`).focus()
        })
      })
    },
    handleMultiselectClick (event, fieldPath, value) {
      // Check if fieldMatches[fieldPath] is an array; if not, initialize it as an empty array
      const fieldMatches = Array.isArray(this.filterSettings.fieldMatches[fieldPath])
        ? this.filterSettings.fieldMatches[fieldPath]
        : []

      // If the value is not already selected, add it to the array
      if (!fieldMatches.includes(value)) {
        fieldMatches.push(value)
      } else {
        // If the value is already in the array, remove it
        fieldMatches.splice(fieldMatches.indexOf(value), 1)
      }

      // Update the fieldMatches object to trigger reactivity
      this.filterSettings.fieldMatches[fieldPath] = fieldMatches
      // enforce multiselect valueType - gets lost in the ReviewApp
      this.filterSettings.valueTypes[fieldPath] = 'multiobject'

      // Trigger a search with the updated filters
      this.doSearch()
    },
    deselectMultiselectTag (field, value) {
      // Make sure the field exists
      if (!this.filterSettings.fieldMatches[field]) return

      // Remove value from array
      this.filterSettings.fieldMatches[field] = this.filterSettings.fieldMatches[field].filter(v => v !== value)

      // if the array is empty, remove the field
      if (this.filterSettings.fieldMatches[field].length === 0) {
        delete this.filterSettings.fieldMatches[field]
      }

      // Trigger a search with the updated filters
      this.doSearch()
    },
    multiselectMatchesSearch (multiselectName, label) {
      const query = this.multiselectSearchQueries[multiselectName] || ''
      return label.toLowerCase().includes(query.toLowerCase())
    },
    countFilteredMultiselectItems (multiselectName) {
      const valueChoices = this.search.filters.flatMap(f => f.items)
        .find(item => item.name === multiselectName)?.valueChoices || []
      const filteredItems = valueChoices.filter(choice =>
        this.multiselectMatchesSearch(multiselectName, choice.label)
      )

      return filteredItems.length
    },
    scrollToSearchResultTop () {
      const yOffset = -70
      const searchResultTop = this.$refs.teaserGroupWrapper.getBoundingClientRect().top
      const y = searchResultTop + window.scrollY + yOffset
      window.scrollTo({ top: y })
    },
    initDefaults () {
      this.search?.filters?.forEach((group) => {
        group?.items?.forEach((item) => {
          if (item.valueType !== undefined) {
            this.filterSettings.valueTypes[item.fieldPath] = item.valueType
          }
          if (item.nestedPath !== undefined) {
            this.filterSettings.nestedPaths[item.fieldPath] = item.nestedPath
          }
          if (['bubblechooser', 'select', 'radiogroup'].includes(item.type) && item.fieldPath && item.valueChoices) {
            const defaultItem = item.valueChoices.find((i) => i.default)
            if (defaultItem) {
              this.filterSettings.fieldMatches[item.fieldPath] = defaultItem.value
            }
          }
          if (item.type === 'multiselect' && item.fieldPath) {
            this.filterSettings.fieldMatches[item.fieldPath] = []
          }
        })
      })
    },
    fetchPageOffset () {
      const hashSplit = window.location.hash.split('#')
      if (hashSplit.length >= 2) {
        for (let i = 1; i < hashSplit.length; i++) {
          const hashParam = hashSplit[i].split('=')
          if (hashParam[0] === 'page' && hashParam[1]) {
            this.offset = ((Number(hashParam[1]) || 1) - 1) * this.limit
            break
          }
        }
      }
    },

    /**
     * Fills datalayer and sends pageview to GA
     */

    triggerGAForSearchResult () {
      const dataLayerEvent = {
        event: 'search_result_pageview'
      }
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(dataLayerEvent)
    },
    async doSearch (trigger = undefined) {
      if (this.progressSearch || import.meta.env.SSR) {
        return
      }

      // while timing is everything in this async logic, we need a flag to prevent early ad reloads
      // we explicitly have to wait until content is completely loaded
      this.setAsyncContentReadyForAdReload(false) // reset the async content ready for reload flag

      this.progressSearch = true
      this.searchErrorMessage = ''
      this.teasers = []

      if (this.searchPhrase !== this.filterSettings.latestSearchPhrase) {
        const url = new URL(window.location)
        url.searchParams.set('query', cleanUpSearchPhrase(this.searchPhrase))
        url.hash = ''
        this.teasers = []
        window.history.pushState({}, '', url + window.location.hash)
        this.offset = 0
        this.paginationUrl = window.location.pathname + window.location.search
      }

      if (['keyword', 'pager', 'hashchange'].includes(trigger)) {
        this.adaSlotReloadOnInteraction(this.reloadConfig)
      }

      if (['hashchange'].includes(trigger)) {
        this.scrollToSearchResultTop()
      }

      // reset to first page if doSearch is triggered unequal hashchange and initial pageView (while changing filters) preventing pages without teasers
      if (!['pageView', 'hashchange'].includes(trigger)) {
        history.replaceState(null, document.title, location.pathname + location.search)
        this.offset = 0
      }

      // create the CAPI query based on the provided fields and weights (if available)
      const params = {}

      params.esDsl = {
        bool: {
          must: []
        }
      }

      const maxBoost = this.search.searchFieldPaths.reduce((acc, p) => {
        const [, boost = 1] = p.split('^') // extracts the weight part if specified
        return Math.max(acc, parseInt(boost))
      }, 1)
      // in case of fuzzy search we need to weight a match a higher boost value than the fuzzy match
      // therefore we have to search the same field twice with different boost values
      if (this.searchPhrase) {
        const searchFieldsEsMapping = this.search.searchFieldPaths.reduce((accu, p) => {
          const [path, boost = 1] = p.split('^') // extracts the weight part if specified
          accu.push({
            match: {
              [path]: {
                query: this.searchPhrase || '*',
                boost: maxBoost + parseInt(boost), // the non fuzzy match should have a higher a higher boost than the fuzzy match
                operator: 'AND'
              }
            }
          })
          accu.push({
            match: {
              [path]: {
                query: this.searchPhrase || '*',
                fuzziness: 'AUTO',
                minimum_should_match: '2',
                boost: parseInt(boost)
              }
            }
          })
          return accu
        }, [])
        params.esDsl.bool.must.push({
          bool: {
            should: searchFieldsEsMapping
          }
        })
      }

      /**
       * nestedPath Filter Code-Block - Alpha Version - START
       */

      // while we want to process the nested filters separately, we need to skip them in the next step
      // extract nestedPaths of all filters
      const nestedPaths = Object.values(this.filterSettings.nestedPaths || {})
      const uniqueNestedPaths = [...new Set(nestedPaths)] // remove duplicates

      // but while we only want to process nested with at least two selected filters, we need to count them to make a decision
      const nestedFilters = uniqueNestedPaths.map(path => {
        const activeFilters = Object.keys(this.filterSettings.nestedPaths)
          .filter(key => this.filterSettings.nestedPaths[key] === path)
          .map(field => ({
            fieldPath: field,
            values: this.filterSettings.fieldMatches[field] || []
          }))
          .filter(filter => filter.values.length > 0)

        return { nestedPath: path, activeFilterCount: activeFilters.length, activeFilters }
      })

      // Add nested object only if more than 1 filter is set otherwise process them in the next step
      nestedFilters.forEach((group, nestedFilterIndex) => {
        if (group.activeFilterCount > 1) {
          // we create the nested object for the group with multiple active filters
          const nestedQuery = {
            nested: {
              path: group.nestedPath, // add the nestedPath to the query
              query: {
                bool: {
                  must: [] // here we will add the filters for the nestedPath
                }
              }
            }
          }

          // ⚠️ Note: This logic assumes that `nestedFilters[0].activeFilters[0].values`
          // always refers to the 'provider' filter group and its values.
          // This is not a generic solution – it's a quick fix that relies on the current structure
          // and ordering of filters. Should be refactored if filter order or schema changes.

          // Check if 'amazonprime' is included in the active provider filters to ensure
          // that 'amazon' is automatically synced accordingly in the UI filter logic
          const providerActiveFilters = nestedFilters[0].activeFilters[0].values

          group.activeFilters.forEach((filter, activeFilterIndex) => {
            const filterValues = [...filter.values]

            // If 'amazonprime' is active, make sure 'amazon' is also included,
            // as both providers are considered a unit in our VOD context
            if (nestedFilterIndex === 0 && activeFilterIndex === 0 && providerActiveFilters.includes('amazonprime')) {
              filterValues.push('amazon')
            } else if (!providerActiveFilters.includes('amazonprime') && filterValues.includes('amazon')) {
              // If 'amazonprime' is no longer active, remove 'amazon' to avoid an inconsistent filter state
              filterValues.splice(filterValues.indexOf('amazon'), 1)
            }

            nestedQuery.nested.query.bool.must.push({
              bool: {
                should: filterValues.map(val => ({
                  match: {
                    [filter.fieldPath]: val
                  }
                })),
                minimum_should_match: 1
              }
            })
          })

          // at least, we add the nestedQuery to the main query
          params.esDsl.bool.must.push(nestedQuery)
        }
      })

      /**
       * nestedPath Filter Code-Block - Alpha Version - END
       */

      Object.keys(this.filterSettings.fieldMatches).forEach(fieldPath => {
        const value = this.filterSettings.fieldMatches[fieldPath]
        const valueType = this.filterSettings.valueTypes[fieldPath]
        const nestedPath = this.filterSettings.nestedPaths[fieldPath]

        // Special case for VOD: If 'amazonprime' was selected, 'amazon' should also be passed (or removed if 'amazonprime' is deselected),
        // since both services technically belong together and are used together for better UX
        if (fieldPath === 'documentData.justWatch.offers.provider.technicalName') {
          const hasAmazonPrime = value.includes('amazonprime')
          const hasAmazon = value.includes('amazon')

          if (hasAmazonPrime && !hasAmazon) {
            value.push('amazon')
          } else if (!hasAmazonPrime && hasAmazon) {
            value.splice(value.indexOf('amazon'), 1)
          }
        }

        if (valueType === 'multiobject') {
          // skip nested filters if there are at least two selected for that filter
          // we will process them in the next step separately
          if (nestedFilters.find(nestedFilter => nestedFilter.nestedPath === nestedPath)?.activeFilterCount > 1) {
            return
          }

          // Create a must condition that will contain multiple OR conditions
          const multiobjectMust = {
            bool: {
              should: [], // Holds the OR conditions for the selected values
              minimum_should_match: 1 // Ensures at least one value must match
            }
          }

          value.forEach(value => {
            // Add a match condition for each selected value, escaping dots to avoid ES query issues
            multiobjectMust.bool.should.push({
              match: {
                [fieldPath]: `"${value.replaceAll('.', '\\.')}"` // Ensure values with dots are properly escaped
              }
            })
          })

          // Only add the must condition if there are selected values and fuzzy search is enabled
          if (value.length > 0) {
            params.esDsl.bool.must.push(multiobjectMust)
          }
        }
      })

      const originalQuery = JSON.parse(JSON.stringify(params.esDsl))

      // NOTE
      // for vod searches without any phrase submitted by the user, we don't want any unrelevant/shitty results for them to see in the top result set.
      // this query exludes those undesired results preemptively based on tmdb popularity, vote count, vote average and release year / air date.
      // however, once a user has entered a particular search phrase, we mustn't apply this query anymore, as it might be preventing them from finding
      // the actual content they are looking for.
      //
      // EXMAPLE
      // https://bauerxcel.atlassian.net/browse/NXT-8156
      // the movie "Rheingold" has a vote average of 6.5, but the condition says `gt: 6.5`, hence it could not be found.
      //
      // TODO
      // refactor the vod search altogether, as it is a bit messy right now: https://bauerxcel.atlassian.net/browse/NXT-7752
      const noSearchPhraseQuery = {
        bool: {
          should: [
            {
              range: {
                'documentData.tmdb.popularity': {
                  gt: 300,
                  boost: 2.0
                }
              }
            },
            {
              range: {
                'documentData.tmdb.voteCount': {
                  gt: 100,
                  boost: 0.3
                }
              }
            },
            {
              range: {
                'documentData.tmdb.voteAverage': {
                  gt: 6.5,
                  boost: 0.3
                }
              }
            },
            {
              bool: {
                filter: [
                  { term: { 'documentData.justWatch.objectType': 'movie' } }
                ],
                must: [
                  {
                    range: {
                      'documentData.justWatch.originalReleaseYear': {
                        gte: new Date().getFullYear() - 0.2,
                        boost: 1.4
                      }
                    }
                  }
                ]
              }
            },
            {
              bool: {
                filter: [
                  { term: { 'documentData.justWatch.objectType': 'show' } }
                ],
                must: [
                  {
                    range: {
                      'documentData.tmdb.lastAirDate': {
                        gte: new Date().getFullYear() - 0.2 + '-01-01',
                        boost: 1.4
                      }
                    }
                  }
                ]
              }
            }
          ],
          minimum_should_match: 0 // Make these optional boosting factors
        }
      }

      params.esDsl = {
        bool: {
          must: [
            originalQuery
          ]
        }
      }

      if (!this.searchPhrase) {
        params.esDsl.bool.must.push(noSearchPhraseQuery)
      }

      params.esDsl.bool.must.push({
        exists: {
          field: 'documentData.tmdb.id'
        }
      })
      params.esDsl.bool.must.push({
        exists: {
          field: 'documentData.justWatch.offers'
        }
      })

      params.sort = '_score:desc,documentData.tmdb.voteCount:desc,documentData.tmdb.voteAverage:desc'
      params.brand = this.brandFromStore
      params.type = this.search.assetType || 'article'
      params.offset = this.offset
      params.limit = this.limit
      params.teaserImageFormat = 'imagePortrait'
      params.esDsl = JSON.stringify(params.esDsl)

      try {
        const response = await axios.get(this.rsConfig.searchServiceUrl, { params })
        this.numFound = response.data.total
        this.teasers = response.data.results
        this.filterSettings.latestSearchPhrase = this.searchPhrase
        window.localStorage.setItem('vodSearchFilterSettings', JSON.stringify(this.filterSettings))
      } catch (e) {
        this.searchErrorMessage = 'Fehler bei der Suchanfrage. Bitte den Suchbegriff ändern.'
        console.error('error while searching: ', e)
      }

      this.$refs.rootDiv.querySelector('.bx-form__button')?.blur()
      this.progressSearch = false
    },
    // In order to be able to sort by score AND some other field, we need to convert the query to a constant score query.
    // Otherwise score has a different value for each hit and we can't sort be the additional field
    // https://www.elastic.co/guide/en/elasticsearch/reference/current/query-dsl-constant-score-query.html
    // written by co-pilot
    deepConvertToConstantScore (query) {
      // Check if the current part of the query is an object and not null
      if (typeof query === 'object' && query !== null) {
        for (const key in query) {
          // Check if this part of the query should be converted
          if (key === 'match') {
            return {
              constant_score: {
                filter: { [key]: query[key] },
                boost: query[key][Object.keys(query[key])[0]].boost
              }
            }
          } else {
            // If it shouldn't be converted, but is an object/array, recurse into it
            query[key] = this.deepConvertToConstantScore(query[key])
          }
        }
      }
      return query
    }
  }
}
</script>
